import axios from "axios";

const api = axios.create({
  baseURL: "/api", // same-origin
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// Attach auth token (if present)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* ---------- tiny util ---------- */
function buildQuery(params = {}) {
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qp.set(k, String(v));
  });
  const s = qp.toString();
  return s ? `?${s}` : "";
}

/* ========= Stake API helpers ========= */

/**
 * Create a new stake on a post
 * backend route: POST /posts/:postId/stakes?network=mainnet|testnet
 * headers: (optional) X-Idempotency-Key, X-Network
 *
 * opts = {
 *   staker: string,            // your backend still expects this username
 *   amountPi: number,
 *   durationHours?: number,    // default 24 on backend
 *   reference?: string,        // optional custom reference
 *   network?: 'mainnet'|'testnet',
 *   idempotencyKey?: string
 * }
 */
export function createStake(postId, opts = {}) {
  const { network, idempotencyKey, ...payload } = opts;
  const headers = {};
  if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;
  if (network) headers["X-Network"] = network; // resolveNet also reads this
  const qs = buildQuery({ network });
  return api.post(`/posts/${postId}/stakes${qs}`, payload, { headers });
}

/**
 * (future) Increase an existing stake
 */
export const increaseStake = (payload) =>
  api.post(`/stakes/increase`, payload); // placeholder until backend route exists

/**
 * Get all stakes made by the current user (paginated)
 * GET /posts/stakes/my
 * params = { page?, limit?, network? }
 */
export function getMyStakes(params = {}) {
  const { network, ...rest } = params;
  const headers = {};
  if (network) headers["X-Network"] = network;
  return api.get(`/posts/stakes/my`, { params: rest, headers });
}

/**
 * Get the current user’s stakes on a specific post
 * GET /posts/:postId/stakes/mine
 * params = { page?, limit?, network? }
 */
export function getMyStakesForPost(postId, params = {}) {
  const { network, ...rest } = params;
  const headers = {};
  if (network) headers["X-Network"] = network;
  return api.get(`/posts/${postId}/stakes/mine`, { params: rest, headers });
}

/**
 * Cancel a stake by its id (future route)
 * POST /stakes/:stakeId/cancel
 */
export const cancelStake = (stakeId) =>
  api.post(`/stakes/${stakeId}/cancel`);

/**
 * Fetch public stake summary for a specific post (visible to everyone)
 * GET /posts/:postId/stakes/summary?viewer=<username>&network=<net>
 *
 * opts = { viewer?: string, network?: 'mainnet'|'testnet' }
 */
export function getStakeSummary(postId, opts = {}) {
  const { viewer, network } = opts;
  const headers = {};
  if (network) headers["X-Network"] = network;
  const qs = buildQuery({ viewer, network });
  return api.get(`/posts/${postId}/stakes/summary${qs}`, { headers });
}


// Add near the other stake helpers
export function listStakesByPost(postId, params = {}) {
  const { network, ...rest } = params;
  const headers = {};
  if (network) headers["X-Network"] = network; // resolves net on backend
  return api.get(`/posts/${postId}/stakes`, { params: rest, headers });
}


// ✅ Back-compat alias for older imports
export { getStakeSummary as getStakeSummaryForPost };

/* ========= Export default axios instance ========= */
export default api;
